import { useCallback, useEffect, useRef, useState } from "react";

import {
  type InfiniteData,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { v5 as uuidv5 } from "uuid";

import {
  AWAITING_SESSION_TIMEOUT_MS,
  copilotSessionKeys,
  SESSION_CACHE_KEY_PREFIX,
  TERMINAL_STATUSES,
  useCopilotInvoke,
  useCopilotMessages,
} from "src/api/agents/copilot";
import type { ToolCallWithResult } from "src/base-components/agents/ToolCallDisplay";
import { caseHintsKeys } from "src/cases/queries/hints";
import {
  Environment,
  type SessionMessagesResponse,
  type SessionMessagesResponseMessagesInner,
  type ToolCallContentBlock,
  type ToolMessage,
  ToolType,
} from "src/clients/agent-management";
import { toastActions } from "src/design-system/Toast/utils";
import type { DashboardPageParams } from "src/router/urls";
import { formatTerminationReason } from "src/utils/agents/formatTerminationReason";
import { useParamsDecode } from "src/utils/useParamsDecode";

export type CopilotChatMessage =
  | { id: string; role: "user"; content: string }
  | { id: string; role: "assistant"; content: string }
  | {
      id: string;
      role: "tool-group";
      toolCallsWithResults: ToolCallWithResult[];
    };

const HIDDEN_MESSAGE_TAGS = ["files", "eager_investigation_message"];

const isHiddenSystemMessage = (text: string): boolean =>
  HIDDEN_MESSAGE_TAGS.some((tag) => text.startsWith(`<${tag}>`));

const extractMessages = (
  sessionMessages: SessionMessagesResponseMessagesInner[],
): CopilotChatMessage[] => {
  const toolResultMap = new Map<string, ToolMessage>();
  for (const msg of sessionMessages) {
    if (msg.type === "tool") {
      toolResultMap.set(msg.tool_call_id, msg);
    }
  }

  const result: CopilotChatMessage[] = [];

  for (const msg of sessionMessages) {
    if (msg.type === "human") {
      const text =
        msg.invocation_message ??
        (typeof msg.content === "string" ? msg.content : "");
      if (text && !isHiddenSystemMessage(text)) {
        result.push({ id: msg.id ?? text, role: "user", content: text });
      }
    } else if (msg.type === "ai") {
      const blocks = msg.content_blocks ?? [];

      const textContent = blocks
        .filter((b) => b.type === "text")
        .map((b) => b.text)
        .filter((text): text is string => text !== undefined && text !== null)
        .join("\n\n");

      // The structured-output tool (json_response) is the agent's final answer,
      // not a user-facing tool call — keep it out of the chat transcript.
      const toolCalls = blocks.filter(
        (b) =>
          b.type === "tool_call" &&
          (b as ToolCallContentBlock).metadata.type !==
            ToolType.STRUCTURED_OUTPUT,
      ) as ToolCallContentBlock[];

      const aiId = msg.id ?? "";

      if (toolCalls.length > 0) {
        result.push({
          id: `${aiId}-tools`,
          role: "tool-group",
          toolCallsWithResults: toolCalls.map((tc) => ({
            toolCall: tc,
            toolResult: toolResultMap.get(tc.id) ?? null,
          })),
        });
      }
      if (textContent) {
        result.push({
          id: `${aiId}-text`,
          role: "assistant",
          content: textContent,
        });
      }
    }
  }

  return result;
};

export const hasNewToolMessages = (
  messages: SessionMessagesResponseMessagesInner[],
  lastKnownId: string | null | undefined,
): boolean => {
  const boundaryIndex = lastKnownId
    ? messages.findIndex((m) => m.id === lastKnownId)
    : -1;
  const newMessages =
    boundaryIndex === -1 ? messages : messages.slice(0, boundaryIndex);
  return newMessages.some((m) => m.type === "tool");
};

const selectCopilotMessages = (
  data: InfiniteData<SessionMessagesResponse>,
) => ({
  session: data.pages[0]?.session,
  messages: extractMessages(data.pages.flatMap((p) => p.messages ?? [])),
});

export const sessionKeys = {
  eagerLoading: (userId: string | undefined, caseId: string) =>
    [SESSION_CACHE_KEY_PREFIX, "eager-loading", userId, caseId] as const,
};

const SESSION_NAMESPACE = "a3f2b8c1-7d4e-4f9a-b6e5-1c8d9e0f2a3b";

export const getSessionId = (
  userId: string | undefined,
  caseId: string,
  agentVersionId: string | undefined,
): string | null =>
  userId
    ? uuidv5(
        `${caseId}:${userId}:${agentVersionId ?? "latest"}`,
        SESSION_NAMESPACE,
      )
    : null;

type UseCaseCopilotSessionParams = {
  agentId: string | undefined;
  agentVersionId: string | undefined;
  caseId: string;
  userId: string | undefined;
  environment: string;
  fileIds: string[];
};

export const useCaseCopilotSession = ({
  agentId,
  agentVersionId,
  caseId,
  userId,
  environment,
  fileIds,
}: UseCaseCopilotSessionParams) => {
  const sessionId = getSessionId(userId, caseId, agentVersionId);

  const [hasStarted, setHasStarted] = useState(false);

  const awaitingSessionRef = useRef(false);
  const awaitingTimeoutRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    setHasStarted(false);
    awaitingSessionRef.current = false;
    clearTimeout(awaitingTimeoutRef.current);
  }, [sessionId]);

  const queryClient = useQueryClient();
  const { wsId } = useParamsDecode<DashboardPageParams>();

  const invokeMutation = useCopilotInvoke({
    agentId,
    agentVersionId,
    sessionId,
    source: "case",
    environment:
      environment === "live" ? Environment.PRODUCTION : Environment.SANDBOX,
    extraInvokeData: { case_id: caseId, file_ids: fileIds },
    messagesQueryKey: copilotSessionKeys.messages(sessionId),
    onInvokeStarted: () => {
      setHasStarted(true);
      awaitingSessionRef.current = true;
      clearTimeout(awaitingTimeoutRef.current);
      awaitingTimeoutRef.current = setTimeout(() => {
        awaitingSessionRef.current = false;
      }, AWAITING_SESSION_TIMEOUT_MS);
    },
  });

  const lastKnownMessageIdRef = useRef<string | null | undefined>(undefined);

  const messagesQuery = useCopilotMessages(sessionId, {
    awaitingSessionRef,
    isPending: invokeMutation.isPending,
    select: selectCopilotMessages,
  });

  useEffect(() => {
    const cached = queryClient.getQueryData<
      InfiniteData<SessionMessagesResponse>
    >(copilotSessionKeys.messages(sessionId));
    const rawMessages = cached?.pages?.[0]?.messages ?? [];
    const newestId = rawMessages[0]?.id ?? null;

    if (lastKnownMessageIdRef.current === undefined) {
      lastKnownMessageIdRef.current = newestId;
      return;
    }
    if (newestId === lastKnownMessageIdRef.current) return;

    if (hasNewToolMessages(rawMessages, lastKnownMessageIdRef.current)) {
      queryClient.invalidateQueries({
        queryKey: caseHintsKeys.get(wsId, caseId),
      });
    }
    lastKnownMessageIdRef.current = newestId;
  }, [messagesQuery.dataUpdatedAt, queryClient, wsId, caseId, sessionId]);

  const session = messagesQuery.data?.session;
  const sessionStatus = session?.status;
  const isSessionTerminal =
    sessionStatus != null && TERMINAL_STATUSES.has(sessionStatus);

  const { data: isEagerLoading = false } = useQuery<boolean>({
    queryKey: sessionKeys.eagerLoading(userId, caseId),
    queryFn: () => false,
    enabled: false,
  });

  const messages = messagesQuery.data?.messages ?? [];

  const sendMessage = useCallback(
    (text: string) => {
      if (invokeMutation.isPending) return;
      if (!agentId) {
        toastActions.failure({
          title: "Copilot not available",
          description: "No copilot agent is configured for this case template.",
        });
        return;
      }

      invokeMutation.mutate(text);
    },
    [agentId, invokeMutation],
  );

  const isActive =
    invokeMutation.isPending ||
    awaitingSessionRef.current ||
    (isEagerLoading && (!hasStarted || !isSessionTerminal)) ||
    ((hasStarted || sessionStatus != null) && !isSessionTerminal);

  const failureMessage =
    sessionStatus === "failed"
      ? session?.termination_reason
        ? `Something went wrong: ${formatTerminationReason(session.termination_reason)}`
        : "Something went wrong"
      : null;

  return {
    messages,
    isLoading: messagesQuery.isLoading,
    isActive,
    failureMessage,
    sendMessage,
    fetchNextPage: messagesQuery.fetchNextPage,
    hasNextPage: messagesQuery.hasNextPage,
    isFetchingNextPage: messagesQuery.isFetchingNextPage,
  };
};
